import React, { useState, useEffect, useRef } from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Pagination } from '@/components/Pagination';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useReactToPrint } from 'react-to-print';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

// Helper to format date to DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Report categories for collapsible sections
const reportCategories = [
  {
    id: 'sales',
    label: 'Sales Reports',
    icon: '📊',
    reports: [
      { id: 'monthly_sales', label: 'Monthly Sales', icon: '📊' },
      { id: 'quarterly_sales', label: 'Quarterly Sales', icon: '📈' },
      { id: 'yearly_sales', label: 'Yearly Sales', icon: '📉' },
      { id: 'sales_rep', label: 'Sales Rep Performance', icon: '👤' },
      { id: 'financier', label: 'Financier-wise Report', icon: '🏦' },
      { id: 'lead_source', label: 'Lead Source Analysis', icon: '📍' },
    ]
  },
  {
    id: 'insurance',
    label: 'Insurance Reports',
    icon: '📋',
    reports: [
      { id: 'insurance', label: 'Insurance Report', icon: '📋' },
    ]
  },
  {
    id: 'vehicle',
    label: 'Vehicle Reports',
    icon: '🚗',
    reports: [
      { id: 'vehicle_model', label: 'Vehicle Model Analysis', icon: '🚗' },
      { id: 'color_analysis', label: 'Color Analysis', icon: '🎨' },
      { id: 'missing_data', label: 'Missing Data Report', icon: '⚠️' },
    ]
  }
];

// Flat list for backward compatibility
const reportTypes = reportCategories.flatMap(cat => cat.reports);

function MISReports({ user, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState('monthly_sales');
  
  // Initialize date range with Year-to-Date (Jan 1 to today)
  const getYTDRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    // Jan 1 of current year
    const firstDay = `${year}-01-01`;
    // Today
    const today = `${year}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
      from: firstDay,
      to: today
    };
  };
  
  const [dateRange, setDateRange] = useState(getYTDRange());
  const [companyDetails, setCompanyDetails] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [missingFilter, setMissingFilter] = useState('registration');
  const [shareEmail, setShareEmail] = useState('');
  const [shareWhatsApp, setShareWhatsApp] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['sales', 'insurance', 'vehicle']);
  const [shareFormat, setShareFormat] = useState('excel'); // 'pdf', 'excel', 'word'
  const printRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (bookings.length > 0) {
      generateReportData();
    }
  }, [bookings, selectedReport, dateRange, missingFilter]);

  const fetchData = async () => {
    try {
      const [bookingsRes, companyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings?limit=5000`),
        axios.get(`${BACKEND_URL}/api/masters?type=company`)
      ]);
      setBookings(bookingsRes.data);
      if (companyRes.data && companyRes.data.length > 0) {
        setCompanyDetails(companyRes.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  // Filter by DMS Invoice Date instead of Date of Booking
  const filterByDateRange = (data) => {
    if (!dateRange.from && !dateRange.to) return data;
    return data.filter(b => {
      // Use DMS Invoice Date for filtering
      const invoiceDate = b.dms_invoice_date;
      if (!invoiceDate) return false;
      const date = new Date(invoiceDate);
      const fromDate = dateRange.from ? new Date(dateRange.from) : new Date('1900-01-01');
      const toDate = dateRange.to ? new Date(dateRange.to) : new Date('2100-12-31');
      return date >= fromDate && date <= toDate;
    });
  };

  const generateReportData = () => {
    const filtered = filterByDateRange(bookings);
    setFilteredBookings(filtered);

    switch (selectedReport) {
      case 'monthly_sales':
        generateMonthlySales(filtered);
        break;
      case 'quarterly_sales':
        generateQuarterlySales(filtered);
        break;
      case 'yearly_sales':
        generateYearlySales(filtered);
        break;
      case 'sales_rep':
        generateSalesRepData(filtered);
        break;
      case 'vehicle_model':
        generateVehicleModelData(filtered);
        break;
      case 'color_analysis':
        generateColorData(filtered);
        break;
      case 'financier':
        generateFinancierData(filtered);
        break;
      case 'lead_source':
        generateLeadSourceData(filtered);
        break;
      case 'missing_data':
        generateMissingDataReport(filtered);
        break;
      case 'insurance':
        // Filter only bookings with Policy Status = "Not Received"
        const pendingInsurance = filtered.filter(b => 
          !b.policy_status || b.policy_status === 'Not Received' || b.policy_status === ''
        );
        setReportData(pendingInsurance);
        break;
      default:
        setReportData([]);
    }
  };

  // Generate Monthly Sales based on DMS Invoice Date
  const generateMonthlySales = (data) => {
    const monthlyData = {};
    data.forEach(b => {
      // Use DMS Invoice Date for grouping
      if (b.dms_invoice_date) {
        const date = new Date(b.dms_invoice_date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData[key] = (monthlyData[key] || 0) + 1;
      }
    });
    const result = Object.entries(monthlyData)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, count]) => ({ month, count }));
    setReportData(result);
  };

  // Generate Quarterly Sales based on DMS Invoice Date
  const generateQuarterlySales = (data) => {
    const quarterlyData = {};
    data.forEach(b => {
      // Use DMS Invoice Date for grouping
      if (b.dms_invoice_date) {
        const date = new Date(b.dms_invoice_date);
        const quarter = Math.ceil((date.getMonth() + 1) / 3);
        const key = `${date.getFullYear()}-Q${quarter}`;
        quarterlyData[key] = (quarterlyData[key] || 0) + 1;
      }
    });
    const result = Object.entries(quarterlyData)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([quarter, count]) => ({ quarter, count }));
    setReportData(result);
  };

  // Generate Yearly Sales based on DMS Invoice Date
  const generateYearlySales = (data) => {
    const yearlyData = {};
    data.forEach(b => {
      // Use DMS Invoice Date for grouping
      if (b.dms_invoice_date) {
        const year = new Date(b.dms_invoice_date).getFullYear();
        yearlyData[year] = (yearlyData[year] || 0) + 1;
      }
    });
    const result = Object.entries(yearlyData)
      .sort((a, b) => a[0] - b[0])
      .map(([year, count]) => ({ year, count }));
    setReportData(result);
  };

  const generateSalesRepData = (data) => {
    const repData = {};
    data.forEach(b => {
      const rep = b.sales_representative || 'Unassigned';
      if (!repData[rep]) {
        repData[rep] = { name: rep, count: 0, bookings: [] };
      }
      repData[rep].count++;
      repData[rep].bookings.push(b);
    });
    setReportData(Object.values(repData).sort((a, b) => b.count - a.count));
  };

  const generateVehicleModelData = (data) => {
    const modelData = {};
    data.forEach(b => {
      const model = `${b.brand_name || ''} ${b.model || ''}`.trim() || 'Unknown';
      const variant = b.variant || 'Standard';
      const key = `${model} - ${variant}`;
      if (!modelData[key]) {
        modelData[key] = { name: key, model, variant, count: 0 };
      }
      modelData[key].count++;
    });
    setReportData(Object.values(modelData).sort((a, b) => b.count - a.count));
  };

  const generateColorData = (data) => {
    const colorData = {};
    data.forEach(b => {
      const color = b.color || 'Not Specified';
      colorData[color] = (colorData[color] || 0) + 1;
    });
    const result = Object.entries(colorData)
      .sort((a, b) => b[1] - a[1])
      .map(([color, count]) => ({ color, count }));
    setReportData(result);
  };

  const generateFinancierData = (data) => {
    const financierData = {};
    data.forEach(b => {
      const financier = b.financiers_name || 'Cash/Self';
      if (!financierData[financier]) {
        financierData[financier] = { name: financier, count: 0, bookings: [] };
      }
      financierData[financier].count++;
      financierData[financier].bookings.push(b);
    });
    setReportData(Object.values(financierData).sort((a, b) => b.count - a.count));
  };

  const generateLeadSourceData = (data) => {
    const leadData = {};
    data.forEach(b => {
      const source = b.lead_source || 'Direct';
      leadData[source] = (leadData[source] || 0) + 1;
    });
    const result = Object.entries(leadData)
      .sort((a, b) => b[1] - a[1])
      .map(([source, count]) => ({ source, count }));
    setReportData(result);
  };

  const generateMissingDataReport = (data) => {
    let filtered;
    switch (missingFilter) {
      case 'registration':
        filtered = data.filter(b => !b.registration_number || b.registration_number.trim() === '');
        break;
      case 'chassis':
        filtered = data.filter(b => !b.chassis_no || b.chassis_no.trim() === '');
        break;
      case 'both':
        filtered = data.filter(b => 
          (!b.registration_number || b.registration_number.trim() === '') ||
          (!b.chassis_no || b.chassis_no.trim() === '')
        );
        break;
      default:
        filtered = data;
    }
    setReportData(filtered);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `MIS_Report_${selectedReport}_${new Date().toISOString().split('T')[0]}`,
  });

  const handleExportExcel = async () => {
    try {
      toast.info('Preparing Excel export...');
      const dataToExport = selectedReport === 'insurance' || selectedReport === 'missing_data' 
        ? reportData.map(b => b.id)
        : filteredBookings.map(b => b.id);
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/export-excel-custom`, {
        booking_ids: dataToExport
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MIS_Report_${selectedReport}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported successfully!');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const handleDownload = async (format) => {
    try {
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      const dataToExport = selectedReport === 'insurance' || selectedReport === 'missing_data' 
        ? reportData.map(b => b.id)
        : filteredBookings.map(b => b.id);
      
      const reportTitle = reportTypes.find(r => r.id === selectedReport)?.label || 'Report';
      
      const response = await axios.post(`${BACKEND_URL}/api/reports/download`, {
        booking_ids: dataToExport.slice(0, 1000),
        format: format,
        report_title: reportTitle,
        company_name: companyDetails?.name,
        company_address: [companyDetails?.door_no, companyDetails?.address_1, companyDetails?.city].filter(Boolean).join(', ')
      }, { responseType: 'blob' });
      
      const extensions = { pdf: 'pdf', excel: 'xlsx', word: 'docx' };
      const mimeTypes = { 
        pdf: 'application/pdf', 
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
        word: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      };
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeTypes[format] }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  const handleWhatsAppShare = () => {
    if (!shareWhatsApp) {
      toast.error('Please enter WhatsApp number');
      return;
    }
    const reportTitle = reportTypes.find(r => r.id === selectedReport)?.label || 'Report';
    const summary = `*${companyDetails?.name || 'AutoBook System'} - ${reportTitle}*\n\nTotal Records: ${reportData.length}\nDate: ${new Date().toLocaleDateString('en-GB')}\n\nGenerated from AutoBook System`;
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${shareWhatsApp.replace(/[^0-9]/g, '')}&text=${encodeURIComponent(summary)}`;
    { const _a = document.createElement('a'); _a.href = whatsappUrl; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
    setShowShareModal(false);
  };

  const handleEmailShare = async () => {
    if (!shareEmail) {
      toast.error('Please enter email address');
      return;
    }
    
    const reportTitle = reportTypes.find(r => r.id === selectedReport)?.label || 'Report';
    const bookingIds = selectedReport === 'insurance' || selectedReport === 'missing_data'
      ? reportData.map(b => b.id)
      : filteredBookings.map(b => b.id);
    
    // Generate report summary
    let summary = `${companyDetails?.name || 'AutoBook System'}\n\n`;
    summary += `Report: ${reportTitle}\n`;
    summary += `Total Records: ${reportData.length}\n`;
    summary += `Generated on: ${new Date().toLocaleDateString('en-GB')}\n\n`;
    
    if (dateRange.from || dateRange.to) {
      summary += `Date Range: ${dateRange.from || 'Start'} to ${dateRange.to || 'End'}\n`;
    }
    
    // Add summary based on report type
    if (selectedReport === 'monthly_sales' || selectedReport === 'quarterly_sales' || selectedReport === 'yearly_sales') {
      summary += '\nSales Summary:\n';
      reportData.slice(0, 10).forEach(item => {
        summary += `- ${item.month || item.quarter || item.year}: ${item.count} sales\n`;
      });
    } else if (selectedReport === 'sales_rep') {
      summary += '\nTop Sales Representatives:\n';
      reportData.slice(0, 5).forEach(item => {
        summary += `- ${item.name}: ${item.count} bookings\n`;
      });
    } else if (selectedReport === 'financier') {
      summary += '\nFinancier Distribution:\n';
      reportData.slice(0, 5).forEach(item => {
        summary += `- ${item.name}: ${item.count} bookings\n`;
      });
    }

    // Build company address string
    const companyAddress = [
      companyDetails?.door_no,
      companyDetails?.address_1,
      companyDetails?.address_2,
      companyDetails?.area,
      companyDetails?.city,
      companyDetails?.pincode,
      companyDetails?.state
    ].filter(Boolean).join(', ');

    // Get format-specific file extension and label
    const formatLabels = {
      'pdf': 'PDF',
      'excel': 'Excel',
      'word': 'Word'
    };

    try {
      toast.info(`Sending email with ${formatLabels[shareFormat]} attachment...`);
      const response = await axios.post(`${BACKEND_URL}/api/send-email-report`, {
        to_email: shareEmail,
        subject: `${companyDetails?.name || 'AutoBook'} - ${reportTitle}`,
        report_title: reportTitle,
        report_summary: summary,
        booking_ids: bookingIds.slice(0, 1000),
        include_excel: shareFormat === 'excel',
        include_pdf: shareFormat === 'pdf',
        include_word: shareFormat === 'word',
        attachment_format: shareFormat,
        company_name: companyDetails?.name,
        company_logo: companyDetails?.logo,
        company_address: companyAddress,
        company_phone: companyDetails?.landline_1 || companyDetails?.mobile,
        company_email: companyDetails?.email,
        company_gst: companyDetails?.gst_no,
        company_hashtag: companyDetails?.hashtag
      });
      
      if (response.data.status === 'sent') {
        toast.success(`Email sent successfully to ${shareEmail}`);
      } else if (response.data.status === 'not_configured') {
        toast.info('Opening email client...');
        window.location.href = response.data.mailto_link;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
      const subject = `${companyDetails?.name || 'AutoBook'} - ${reportTitle}`;
      window.location.href = `mailto:${shareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(summary)}`;
    }
    setShowShareModal(false);
  };

  const CompanyHeader = () => (
    <div className="flex justify-between items-start mb-3 pb-2 border-b-2 border-gray-300 print:mb-2 print:pb-1">
      <div className="flex items-start gap-3">
        {companyDetails?.logo && (
          <img src={companyDetails.logo} alt="Logo" className="h-10 w-auto print:h-8" />
        )}
        <div className="text-xs print:text-[9pt] leading-tight">
          <h1 className="text-sm font-bold print:text-xs">{companyDetails?.name || 'Company Name'}</h1>
          <p className="text-gray-600">
            {[companyDetails?.door_no, companyDetails?.building_name, companyDetails?.address_1].filter(Boolean).join(', ')}
          </p>
          <p className="text-gray-600">
            {[companyDetails?.area, companyDetails?.city, companyDetails?.state].filter(Boolean).join(', ')} - {companyDetails?.pincode || ''}
          </p>
          <p className="text-gray-600">
            {companyDetails?.landline_1 && `Ph: ${companyDetails.landline_1}`}
            {companyDetails?.mobile && ` | Mob: ${companyDetails.mobile}`}
            {companyDetails?.email && ` | ${companyDetails.email}`}
          </p>
          <p className="font-medium">
            {companyDetails?.gst_no && `GSTIN: ${companyDetails.gst_no}`}
            {companyDetails?.hashtag && ` | ${companyDetails.hashtag}`}
          </p>
        </div>
      </div>
      <div className="text-right text-xs print:text-[9pt]">
        <p className="font-bold">{reportTypes.find(r => r.id === selectedReport)?.label}</p>
        <p>Print Date: {formatDate(new Date().toISOString())}</p>
        {dateRange.from && <p>From: {formatDate(dateRange.from)}</p>}
        {dateRange.to && <p>To: {formatDate(dateRange.to)}</p>}
      </div>
    </div>
  );

  const renderChart = () => {
    if (reportData.length === 0) return <p className="text-center py-8 text-gray-500">No data available</p>;

    switch (selectedReport) {
      case 'monthly_sales':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reportData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Sales" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'quarterly_sales':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reportData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="quarter" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Sales" fill="#00C49F" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'yearly_sales':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={reportData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" name="Sales" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'color_analysis':
      case 'lead_source':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={reportData.slice(0, 8)}
                dataKey="count"
                nameKey={selectedReport === 'color_analysis' ? 'color' : 'source'}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {reportData.slice(0, 8).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'sales_rep':
      case 'vehicle_model':
      case 'financier':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reportData.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" name="Count" fill="#FF8042" />
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  const renderDataTable = () => {
    if (selectedReport === 'insurance') {
      return (
        <div className="overflow-x-auto">
          <p className="text-sm font-medium mb-2 text-amber-600">Showing only records with Policy Status: Not Received</p>
          <table className="w-full text-xs border-collapse print:text-[8pt]">
            <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
              <tr>
                <th className="border p-1" colSpan="5">Customer Details</th>
                <th className="border p-1" colSpan="6">Vehicle Details</th>
                <th className="border p-1" colSpan="3">Insurance Nominee</th>
                <th className="border p-1 print:hidden">Actions</th>
              </tr>
              <tr className="bg-white/15">
                <th className="border border-white/20 p-1 text-white">B.No</th>
                <th className="border border-white/20 p-1 text-white">Customer Name</th>
                <th className="border border-white/20 p-1 text-white">Mobile</th>
                <th className="border border-white/20 p-1 text-white">Aadhar</th>
                <th className="border border-white/20 p-1 text-white">PAN</th>
                <th className="border border-white/20 p-1 text-white">Vehicle</th>
                <th className="border border-white/20 p-1 text-white">Variant</th>
                <th className="border border-white/20 p-1 text-white">Color</th>
                <th className="border border-white/20 p-1 text-white">Chassis No</th>
                <th className="border border-white/20 p-1 text-white">Engine No</th>
                <th className="border border-white/20 p-1 text-white">Reg No</th>
                <th className="border border-white/20 p-1 text-white">Nominee</th>
                <th className="border border-white/20 p-1 text-white">Relation</th>
                <th className="border border-white/20 p-1 text-white">DOB</th>
                <th className="border border-white/20 p-1 text-white print:hidden">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((b, idx) => (
                <tr key={b.id} className={`transition-colors hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30'}`}>
                  <td className="border p-1">{b.booking_no}</td>
                  <td className="border p-1">{b.salutation} {b.customer_first_name} {b.customer_last_name}</td>
                  <td className="border p-1">{b.mobile_no_1}</td>
                  <td className="border p-1">{b.aadhar_no || '-'}</td>
                  <td className="border p-1">{b.pan_no || '-'}</td>
                  <td className="border p-1">{b.brand_name} {b.model}</td>
                  <td className="border p-1">{b.variant || '-'}</td>
                  <td className="border p-1">{b.color || '-'}</td>
                  <td className="border p-1 font-mono text-[9px]">{b.chassis_no || '-'}</td>
                  <td className="border p-1 font-mono text-[9px]">{b.engine_motor_no || '-'}</td>
                  <td className="border p-1 font-mono">{b.registration_number || '-'}</td>
                  <td className="border p-1">{b.insurance_nominee_name || '-'}</td>
                  <td className="border p-1">{b.relationship || '-'}</td>
                  <td className="border p-1">{formatDate(b.nominee_dob) || '-'}</td>
                  <td className="border p-1 print:hidden">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          const msg = `*INSURANCE POLICY PENDING*\n\n*Customer Details:*\nName: ${b.salutation || ''} ${b.customer_first_name} ${b.customer_last_name}\nF/H Name: ${b.father_husband_name || '-'}\nAddress: ${b.door_no || ''} ${b.building_name || ''} ${b.address_1 || ''}\nArea: ${b.area || '-'}, City: ${b.city || '-'}\nState: ${b.state || '-'}, PIN: ${b.pin_code || '-'}\nMobile: ${b.mobile_no_1}\nWhatsApp: ${b.whatsapp_no_1 || '-'}\nEmail: ${b.email || '-'}\nAadhar: ${b.aadhar_no || '-'}\nPAN: ${b.pan_no || '-'}\n\n*Vehicle Details:*\nVehicle: ${b.brand_name} ${b.model}\nVariant: ${b.variant || '-'}\nColor: ${b.color || '-'}\nFuel Type: ${b.fuel_type || '-'}\nChassis: ${b.chassis_no || '-'}\nEngine/Motor: ${b.engine_motor_no || '-'}\nBattery: ${b.battery_no || '-'}\nCharger: ${b.charger_no || '-'}\nReg No: ${b.registration_number || '-'}\n\n*Insurance Details:*\nNominee: ${b.insurance_nominee_name || '-'}\nRelationship: ${b.relationship || '-'}\nNominee DOB: ${formatDate(b.nominee_dob) || '-'}`;
                          { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?text=${encodeURIComponent(msg)}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
                        }}
                        className="text-green-600 hover:text-green-800 text-xs"
                      >
                        📱 WhatsApp
                      </button>
                      <button
                        onClick={() => {
                          const subject = `Insurance Policy Pending - ${b.customer_first_name} ${b.customer_last_name} - ${b.chassis_no}`;
                          const body = `INSURANCE POLICY PENDING\n\nCustomer Details:\n- Name: ${b.salutation || ''} ${b.customer_first_name} ${b.customer_last_name}\n- Father/Husband: ${b.father_husband_name || '-'}\n- Address: ${b.door_no || ''} ${b.building_name || ''} ${b.address_1 || ''}\n- Area: ${b.area || '-'}, City: ${b.city || '-'}\n- State: ${b.state || '-'}, PIN: ${b.pin_code || '-'}\n- Mobile: ${b.mobile_no_1}\n- WhatsApp: ${b.whatsapp_no_1 || '-'}\n- Email: ${b.email || '-'}\n- Aadhar: ${b.aadhar_no || '-'}\n- PAN: ${b.pan_no || '-'}\n\nVehicle Details:\n- Vehicle: ${b.brand_name} ${b.model}\n- Variant: ${b.variant || '-'}\n- Color: ${b.color || '-'}\n- Fuel Type: ${b.fuel_type || '-'}\n- Chassis No: ${b.chassis_no || '-'}\n- Engine/Motor No: ${b.engine_motor_no || '-'}\n- Battery No: ${b.battery_no || '-'}\n- Charger No: ${b.charger_no || '-'}\n- Registration No: ${b.registration_number || '-'}\n\nInsurance Nominee Details:\n- Nominee Name: ${b.insurance_nominee_name || '-'}\n- Relationship: ${b.relationship || '-'}\n- Nominee DOB: ${formatDate(b.nominee_dob) || '-'}`;
                          window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs"
                      >
                        📧 Email
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'missing_data') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
              <tr>
                <th className="border p-2">B.No</th>
                <th className="border p-2">Customer</th>
                <th className="border p-2">Mobile</th>
                <th className="border p-2">Vehicle</th>
                <th className="border p-2">Chassis No</th>
                <th className="border p-2">Registration No</th>
                <th className="border p-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map(b => (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="border p-2">{b.booking_no}</td>
                  <td className="border p-2">{b.customer_first_name} {b.customer_last_name}</td>
                  <td className="border p-2">{b.mobile_no_1}</td>
                  <td className="border p-2">{b.brand_name} {b.model}</td>
                  <td className="border p-2 font-mono">{b.chassis_no || <span className="text-red-500">MISSING</span>}</td>
                  <td className="border p-2 font-mono">{b.registration_number || <span className="text-red-500">MISSING</span>}</td>
                  <td className="border p-2">{formatDate(b.date_of_booking)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'sales_rep' || selectedReport === 'financier') {
      return (
        <div className="space-y-4">
          {reportData.map(item => (
            <div key={item.name} className="border rounded p-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-bold">{item.name}</h4>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">{item.count} bookings</span>
              </div>
              {item.bookings && item.bookings.length > 0 ? (
                <table className="w-full text-xs">
                  <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                    <tr>
                      <th className="border p-1">B.No</th>
                      <th className="border p-1">Customer</th>
                      <th className="border p-1">Mobile</th>
                      <th className="border p-1">Vehicle</th>
                      <th className="border p-1">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(item.bookings || []).slice(0, 10).map(b => (
                      <tr key={b.id}>
                        <td className="border p-1">{b.booking_no}</td>
                        <td className="border p-1">{b.customer_first_name}</td>
                      <td className="border p-1">{b.mobile_no_1}</td>
                      <td className="border p-1">{b.brand_name} {b.model}</td>
                      <td className="border p-1">{formatDate(b.date_of_booking)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              ) : (
                <p className="text-xs text-gray-500">No booking details available</p>
              )}
              {item.bookings && item.bookings.length > 10 && (
                <p className="text-xs text-gray-500 mt-1">+ {item.bookings.length - 10} more...</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Default table for other reports
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-100">
            <tr>
              {reportData[0] && Object.keys(reportData[0]).map(key => (
                <th key={key} className="border p-2 text-left capitalize">{key.replace(/_/g, ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {Object.values(row).map((val, j) => (
                  <td key={j} className="border p-2">
                    {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="MIS Reports">
        <p>Loading...</p>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="MIS Reports">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left Sidebar - Report Selection with Collapsible Categories */}
        <div className="bg-white p-4 rounded-lg border">
          <h3 className="font-bold mb-4">Select Report</h3>
          <div className="space-y-3">
            {reportCategories.map(category => (
              <div key={category.id} className="border rounded-lg overflow-hidden">
                {/* Category Header - Collapsible */}
                <button
                  onClick={() => setExpandedCategories(prev => 
                    prev.includes(category.id) 
                      ? prev.filter(id => id !== category.id)
                      : [...prev, category.id]
                  )}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium flex items-center gap-2">
                    <span>{category.icon}</span>
                    {category.label}
                  </span>
                  <span className={`transform transition-transform ${expandedCategories.includes(category.id) ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {/* Category Reports - Collapsible Content */}
                {expandedCategories.includes(category.id) && (
                  <div className="bg-white">
                    {category.reports.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setSelectedReport(type.id)}
                        data-testid={`report-${type.id}`}
                        className={`w-full text-left p-3 pl-6 border-t transition-colors ${
                          selectedReport === type.id ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'
                        }`}
                      >
                        <span className="mr-2">{type.icon}</span>
                        {type.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Date Range Filter - Based on DMS Invoice Date */}
          <div className="mt-6 pt-4 border-t">
            <h4 className="font-medium mb-1">Date Range</h4>
            <p className="text-xs text-gray-500 mb-2">Based on DMS Invoice Date</p>
            <div className="space-y-2">
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                className="w-full p-2 border rounded text-sm"
                placeholder="From"
              />
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                className="w-full p-2 border rounded text-sm"
                placeholder="To"
              />
              {(dateRange.from || dateRange.to) && (
                <button
                  onClick={() => setDateRange({ from: '', to: '' })}
                  className="w-full p-2 text-sm text-red-600 hover:bg-red-50 rounded"
                >
                  Clear Dates
                </button>
              )}
            </div>
          </div>

          {/* Missing Data Filter */}
          {selectedReport === 'missing_data' && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="font-medium mb-2">Filter Missing</h4>
              <select
                value={missingFilter}
                onChange={(e) => setMissingFilter(e.target.value)}
                className="w-full p-2 border rounded text-sm"
              >
                <option value="registration">Missing Registration No</option>
                <option value="chassis">Missing Chassis No</option>
                <option value="both">Missing Either</option>
              </select>
            </div>
          )}
        </div>

        {/* Main Content - Report View */}
        <div className="lg:col-span-3 bg-white p-4 rounded-lg border">
          {/* Action Buttons */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {reportTypes.find(r => r.id === selectedReport)?.icon} {reportTypes.find(r => r.id === selectedReport)?.label}
            </h2>
            <div className="flex gap-2 flex-wrap">
              <button onClick={handlePrint} data-testid="print-btn" className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                🖨️ Print
              </button>
              <button onClick={handleExportExcel} data-testid="excel-btn" className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm">
                📊 Excel
              </button>
              <div className="relative group">
                <button data-testid="download-btn" className="px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 text-sm">
                  ⬇️ Download ▼
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white border rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                  <button onClick={() => handleDownload('pdf')} data-testid="download-pdf" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">📄 PDF</button>
                  <button onClick={() => handleDownload('excel')} data-testid="download-excel" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">📊 Excel</button>
                  <button onClick={() => handleDownload('word')} data-testid="download-word" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">📝 Word</button>
                </div>
              </div>
              <button onClick={() => setShowShareModal(true)} data-testid="share-btn" className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm">
                📤 Share
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 p-3 rounded mb-4">
            <p className="text-sm">
              <strong>Total Records:</strong> {reportData.length} | 
              <strong> Total Bookings in Range:</strong> {filteredBookings.length}
            </p>
          </div>

          {/* Printable Report */}
          <div ref={printRef} className="p-4 print:p-0">
            <CompanyHeader />
            
            {/* Chart */}
            {!['insurance', 'missing_data', 'sales_rep', 'financier'].includes(selectedReport) && (
              <div className="mb-6">
                {renderChart()}
              </div>
            )}

            {/* Data Table */}
            {renderDataTable()}

            {/* Footer */}
            <div className="mt-8 pt-4 border-t text-xs text-gray-500 text-center print:mt-4">
              <p>Generated on {formatDate(new Date().toISOString())} | {companyDetails?.name || 'AutoBook System'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Share Report</h3>
            
            <div className="space-y-4">
              {/* Attachment Format Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Attachment Format</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShareFormat('pdf')}
                    data-testid="format-pdf"
                    className={`flex-1 p-3 rounded border-2 transition-colors ${
                      shareFormat === 'pdf' 
                        ? 'border-blue-600 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📄</div>
                    <div className="text-sm font-medium">PDF</div>
                  </button>
                  <button
                    onClick={() => setShareFormat('excel')}
                    data-testid="format-excel"
                    className={`flex-1 p-3 rounded border-2 transition-colors ${
                      shareFormat === 'excel' 
                        ? 'border-green-600 bg-green-50 text-green-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📊</div>
                    <div className="text-sm font-medium">Excel</div>
                  </button>
                  <button
                    onClick={() => setShareFormat('word')}
                    data-testid="format-word"
                    className={`flex-1 p-3 rounded border-2 transition-colors ${
                      shareFormat === 'word' 
                        ? 'border-purple-600 bg-purple-50 text-purple-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📝</div>
                    <div className="text-sm font-medium">Word</div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {shareFormat === 'pdf' && 'PDF - Best for printing and sharing formal reports'}
                  {shareFormat === 'excel' && 'Excel - Best for data analysis and manipulation'}
                  {shareFormat === 'word' && 'Word - Best for editing and customization'}
                </p>
              </div>

              <hr className="my-4" />

              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="flex-1 p-2 border rounded"
                    placeholder="email@example.com"
                    data-testid="share-email-input"
                  />
                  <button onClick={handleEmailShare} data-testid="share-email-btn" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    📧 Email
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">WhatsApp Number</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={shareWhatsApp}
                    onChange={(e) => setShareWhatsApp(e.target.value)}
                    className="flex-1 p-2 border rounded"
                    placeholder="919876543210"
                    data-testid="share-whatsapp-input"
                  />
                  <button onClick={handleWhatsAppShare} data-testid="share-whatsapp-btn" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                    💬 WhatsApp
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Include country code (e.g., 91 for India)</p>
              </div>
            </div>

            <button
              onClick={() => setShowShareModal(false)}
              data-testid="share-modal-close"
              className="w-full mt-4 px-4 py-2 border rounded hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </SalesLayout>
  );
}

export default MISReports;
